import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { merge } from "@modern-js/runtime-utils/merge";
import { RouterProvider, createBrowserRouter, createHashRouter, createRoutesFromElements, useHref, useLocation, useMatches } from "@modern-js/runtime-utils/router";
import { normalizePathname } from "@modern-js/runtime-utils/url";
import * as React from "react";
import { useContext, useEffect, useMemo } from "react";
import { RuntimeReactContext } from "../../core";
import { getGlobalLayoutApp, getGlobalRoutes } from "../../core/context";
import { getGlobalIsRscClient } from "../../core/context";
import { modifyRoutes as modifyRoutesHook, onBeforeCreateRoutes as onBeforeCreateRoutesHook } from "./hooks";
import { createClientRouterFromPayload } from "./rsc-router";
import { createRouteObjectsFromConfig, deserializeErrors, renderRoutes, urlJoin } from "./utils";
var finalRouteConfig = {
  routes: []
};
var beforeCreateRouter = true;
function modifyRoutes(modifyFunction) {
  if (beforeCreateRouter) {
    var originRoutes = finalRouteConfig.routes;
    var newRoutes = modifyFunction(originRoutes);
    finalRouteConfig.routes = newRoutes;
  } else {
    console.error("It is not allowed to modify routes config after create router.");
  }
}
var routerPlugin = function() {
  var userConfig = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
  return {
    name: "@modern-js/plugin-router",
    registryHooks: {
      modifyRoutes: modifyRoutesHook,
      onBeforeCreateRoutes: onBeforeCreateRoutesHook
    },
    setup: function(api) {
      var routesContainer = {
        current: []
      };
      api.onBeforeRender(function(context) {
        if (window._SSR_DATA && userConfig.unstable_reloadOnURLMismatch) {
          var _ssrContext_request;
          var ssrContext = context.ssrContext;
          var currentPathname = normalizePathname(window.location.pathname);
          var initialPathname = (ssrContext === null || ssrContext === void 0 ? void 0 : (_ssrContext_request = ssrContext.request) === null || _ssrContext_request === void 0 ? void 0 : _ssrContext_request.pathname) && normalizePathname(ssrContext.request.pathname);
          if (initialPathname && initialPathname !== currentPathname) {
            var errorMsg = "The initial URL ".concat(initialPathname, " and the URL ").concat(currentPathname, " to be hydrated do not match, reload.");
            console.error(errorMsg);
            window.location.reload();
          }
        }
        context.router = {
          useMatches,
          useLocation,
          useHref
        };
        Object.defineProperty(context, "routes", {
          get: function get() {
            return routesContainer.current;
          },
          enumerable: true
        });
      });
      api.wrapRoot(function(App) {
        var mergedConfig = merge(api.getRuntimeConfig().router || {}, userConfig);
        var _mergedConfig_serverBase = mergedConfig.serverBase, serverBase = _mergedConfig_serverBase === void 0 ? [] : _mergedConfig_serverBase, _mergedConfig_supportHtml5History = mergedConfig.supportHtml5History, supportHtml5History = _mergedConfig_supportHtml5History === void 0 ? true : _mergedConfig_supportHtml5History, _mergedConfig_basename = mergedConfig.basename, basename = _mergedConfig_basename === void 0 ? "" : _mergedConfig_basename, routesConfig = mergedConfig.routesConfig, createRoutes = mergedConfig.createRoutes, future = mergedConfig.future;
        finalRouteConfig = _object_spread({
          routes: getGlobalRoutes(),
          globalApp: getGlobalLayoutApp()
        }, routesConfig);
        if (!finalRouteConfig.routes && !createRoutes) {
          return App;
        }
        var selectBasePath = function(pathname) {
          var match = serverBase.find(function(baseUrl) {
            return isSegmentPrefix(pathname, baseUrl);
          });
          return match || "/";
        };
        var RouterWrapper = function(props) {
          var _useRouterCreation = useRouterCreation(_object_spread_props(_object_spread({}, props), {
            rscPayload: props === null || props === void 0 ? void 0 : props.rscPayload
          }), {
            api,
            createRoutes,
            supportHtml5History,
            selectBasePath,
            basename,
            future
          }), router = _useRouterCreation.router, routes = _useRouterCreation.routes;
          useEffect(function() {
            routesContainer.current = routes;
          }, [
            routes
          ]);
          beforeCreateRouter = false;
          var RouterContent = function() {
            return /* @__PURE__ */ _jsxs(_Fragment, {
              children: [
                /* @__PURE__ */ _jsx(RouterProvider, {
                  router,
                  future
                }),
                /* @__PURE__ */ _jsx(EmptyComponent, {}),
                /* @__PURE__ */ _jsx(EmptyComponent, {})
              ]
            });
          };
          return App ? /* @__PURE__ */ _jsx(App, {
            children: /* @__PURE__ */ _jsx(RouterContent, {})
          }) : /* @__PURE__ */ _jsx(RouterContent, {});
        };
        return RouterWrapper;
      });
    }
  };
};
var EmptyComponent = function() {
  return null;
};
var safeUse = function(promise) {
  var useProp = "use";
  var useHook = React && React[useProp];
  if (typeof useHook === "function") {
    return useHook(promise);
  }
  return null;
};
function normalizeBase(b) {
  if (b.length > 1 && b.endsWith("/"))
    return b.slice(0, -1);
  return b || "/";
}
function isSegmentPrefix(pathname, base) {
  var b = normalizeBase(base);
  var p = pathname || "/";
  return p === b || p.startsWith("".concat(b, "/"));
}
function useRouterCreation(props, options) {
  var api = options.api, createRoutes = options.createRoutes, supportHtml5History = options.supportHtml5History, selectBasePath = options.selectBasePath, basename = options.basename;
  var runtimeContext = useContext(RuntimeReactContext);
  var baseUrl = selectBasePath(location.pathname).replace(/^\/*/, "/");
  var _basename = baseUrl === "/" ? urlJoin(baseUrl, runtimeContext._internalRouterBaseName || basename) : baseUrl;
  var getBlockNavState = runtimeContext.unstable_getBlockNavState;
  var rscPayload = (props === null || props === void 0 ? void 0 : props.rscPayload) ? safeUse(props.rscPayload) : null;
  var hydrationData = window._ROUTER_DATA || rscPayload;
  return useMemo(function() {
    if (hydrationData === null || hydrationData === void 0 ? void 0 : hydrationData.errors) {
      hydrationData = _object_spread_props(_object_spread({}, hydrationData), {
        errors: deserializeErrors(hydrationData.errors)
      });
    }
    var isRscClient = getGlobalIsRscClient();
    var routes = null;
    if (isRscClient) {
      routes = createRoutes ? createRoutes() : createRouteObjectsFromConfig({
        routesConfig: finalRouteConfig
      });
    } else {
      routes = createRoutes ? createRoutes() : createRoutesFromElements(renderRoutes({
        routesConfig: finalRouteConfig,
        props
      }));
    }
    if (!routes) {
      routes = [];
    }
    var hooks = api.getHooks();
    if (rscPayload) {
      try {
        var router = createClientRouterFromPayload(rscPayload, routes, _basename);
        return {
          router,
          routes: router.routes || []
        };
      } catch (e) {
        console.error("Failed to create router from RSC payload:", e);
      }
    }
    var modifiedRoutes = hooks.modifyRoutes.call(routes);
    var router1 = supportHtml5History ? createBrowserRouter(modifiedRoutes, {
      basename: _basename,
      hydrationData
    }) : createHashRouter(modifiedRoutes, {
      basename: _basename,
      hydrationData
    });
    var originSubscribe = router1.subscribe;
    router1.subscribe = function(listener) {
      var wrappedListener = function() {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        var blockRoute = getBlockNavState ? getBlockNavState() : false;
        if (blockRoute) {
          return;
        }
        return listener.apply(void 0, _to_consumable_array(args));
      };
      return originSubscribe(wrappedListener);
    };
    return {
      router: router1,
      routes: modifiedRoutes
    };
  }, [
    finalRouteConfig,
    props,
    _basename,
    hydrationData,
    getBlockNavState
  ]);
}
export {
  beforeCreateRouter,
  finalRouteConfig,
  modifyRoutes,
  routerPlugin
};
